/**
 * Cities Query Hooks
 * Hooks for fetching location/city data
 */

import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@shared/lib/api/client'
import { API_ENDPOINTS } from '@shared/lib/api/endpoints'
import type { City } from '@shared/types'

// ============================================================================
// Query Keys
// ============================================================================

export const citiesKeys = {
  all: ['cities'] as const,
  lists: () => [...citiesKeys.all, 'list'] as const,
  list: (filters?: string) => [...citiesKeys.lists(), filters] as const,
  details: () => [...citiesKeys.all, 'detail'] as const,
  detail: (id: string) => [...citiesKeys.details(), id] as const,
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Get all cities
 * Returns list of cities for location filtering
 */
export function useCities() {
  return useQuery({
    queryKey: citiesKeys.lists(),
    queryFn: async () => {
      // API returns array directly in data field (not wrapped in { cities: [] })
      // API uses _id but our types use id, so we need to map the response
      interface CityDTO {
        _id: string
        name: string
        province?: string
        createdAt?: string
        updatedAt?: string
      }
      const response = await apiClient.get<CityDTO[]>(API_ENDPOINTS.locations.cities)

      // Map _id to id and transform to City type
      const cities: City[] = response.map((city) => ({
        id: city._id,
        name: city.name,
        provinceId: city.province,
        createdAt: city.createdAt || '',
        updatedAt: city.updatedAt || '',
      }))

      // Sort alphabetically
      return cities.sort((a, b) => a.name.localeCompare(b.name))
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (cities rarely change)
  })
}

/**
 * Get single city by ID
 */
export function useCity(id: string) {
  return useQuery({
    queryKey: citiesKeys.detail(id),
    queryFn: () => apiClient.get<City>(API_ENDPOINTS.locations.cityById(id)),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  })
}
